import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface NavbarHandlerProps {
    setIsScrolled: React.Dispatch<React.SetStateAction<boolean>>;
}

// Drives the --nav-bg-opacity CSS variable from 0 → 1 over the first 120px of
// scroll on the landing page. The .header-bg rule reads this variable to fade
// the navy background, blur, and border in continuously instead of snapping.
// isScrolled is preserved as a boolean for any downstream consumer; it flips
// at the midpoint of the fade (~60px) where the bg is roughly half-opacity.
const SCROLL_RAMP_PX = 120;

const NavbarHandler: React.FC<NavbarHandlerProps> = ({ setIsScrolled }) => {
    const location = useLocation();

    useEffect(() => {
        const root = document.documentElement;
        const onLanding = location.pathname === "/";

        // Off-landing: nav is always solid. Set once, no scroll listener needed.
        if (!onLanding) {
            root.style.setProperty('--nav-bg-opacity', '1');
            setIsScrolled(true);
            return;
        }

        let rafId: number | null = null;
        const update = () => {
            const maxScroll = Math.max(
                document.documentElement.scrollHeight,
                document.body.scrollHeight,
            ) - window.innerHeight;
            if (maxScroll <= 8) {
                root.style.setProperty('--nav-bg-opacity', '1');
                setIsScrolled(true);
                rafId = null;
                return;
            }

            const opacity = Math.min(
                1,
                Math.max(0, window.pageYOffset / SCROLL_RAMP_PX)
            );
            root.style.setProperty('--nav-bg-opacity', opacity.toFixed(3));
            setIsScrolled(opacity > 0.5);
            rafId = null;
        };

        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(update);
        };

        // Initialize for refresh-mid-scroll.
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            root.style.removeProperty('--nav-bg-opacity');
        };
    }, [setIsScrolled, location]);

    return null;
};

export default NavbarHandler;
